import React, { useEffect, useState } from "react";
import "../../../styles/Header-css/ServicePartner/PartnerVerification/R2rKitStep.css";
import { worklocation } from "../../../../api/partner/worklocation";
import {
  getStates,
  getCities,
  getAreas,
} from "../../../../api/location/locationApi";

const R2rKitStep = ({ data, onDataChange, onBack, onNext }) => {
  /* ---------- MASTER DATA ---------- */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  /* ---------- SELECTION ---------- */
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedAreas, setSelectedAreas] = useState([]);

  const [selectedRadius, setSelectedRadius] = useState(5);
  const [bufferRadius, setBufferRadius] = useState(0);
  const [saving, setSaving] = useState(false);

  /* ---------- LOAD STATES ---------- */
  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await getStates(Number(1));
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setStates(list);
      } catch (e) {
        console.error("Failed to load states", e);
        setStates([]);
      }
    };
    loadStates();
  }, []);

  /* ---------- LOAD CITIES ---------- */
  useEffect(() => {
    if (!selectedState) return;

    const loadCities = async () => {
      try {
        const res = await getCities(selectedState);
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setCities(list);
        setSelectedCity("");
        setAreas([]);
        setSelectedAreas([]);
      } catch (e) {
        console.error("Failed to load cities", e);
        setCities([]);
      }
    };
    loadCities();
  }, [selectedState]);

  /* ---------- LOAD AREAS ---------- */
  useEffect(() => {
    if (!selectedCity) return;

    const loadAreas = async () => {
      try {
        const res = await getAreas(selectedCity);
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        setAreas(list);
        setSelectedAreas([]);
      } catch (e) {
        console.error("Failed to load areas", e);
        setAreas([]);
      }
    };
    loadAreas();
  }, [selectedCity]);

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async () => {
    if (!isFormComplete) {
      alert("Please complete all required fields");
      return;
    }

    const dto = {
      cityId: Number(selectedCity),
      areaIds: selectedAreas.map((a) => a.areaId),
      radius: selectedRadius,
      bufferRadius,
    };

    try {
      setSaving(true);
      await worklocation(dto);
      onNext();
    } catch (e) {
      console.error("Work location save failed", e);
      alert("Failed to save work location");
    } finally {
      setSaving(false);
    }
  };

  const isFormComplete =
    selectedState &&
    selectedCity &&
    selectedAreas.length > 0 &&
    selectedRadius > 0;

  return (
    <>
      <h2>R2R Kit & Location Setup</h2>

      {/* ---------- STATE ---------- */}
      <div className="onboarding-form-group">
        <label>State *</label>
        <select
          className="onboarding-form-control"
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
        >
          <option value="">Select State</option>
          {states.map((s) => (
            <option key={s.stateId} value={s.stateId}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* ---------- CITY ---------- */}
      <div className="onboarding-form-group">
        <label>City *</label>
        <select
          className="onboarding-form-control"
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          disabled={!cities.length}
        >
          <option value="">Select City</option>
          {cities.map((c) => (
            <option key={c.cityId} value={c.cityId}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* ---------- AREAS ---------- */}
      <div className="onboarding-form-group">
        <label>Select Areas *</label>
        {areas.map((area) => {
          const checked = selectedAreas.some(
            (a) => a.areaId === area.areaId
          );

          return (
            <div
              key={area.areaId}
              className="onboarding-checkbox-row"
              onClick={() =>
                setSelectedAreas((prev) =>
                  checked
                    ? prev.filter((a) => a.areaId !== area.areaId)
                    : [...prev, area]
                )
              }
            >
              <div
                className={`onboarding-checkbox ${
                  checked ? "onboarding-checkbox-checked" : ""
                }`}
              >
                {checked && "✓"}
              </div>
              <span className="onboarding-checkbox-label">
                {area.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* ---------- RADIUS ---------- */}
      <div className="onboarding-form-group">
        <label>Radius (km)</label>
        <div className="onboarding-chip-row">
          {[5, 8, 10, 15].map((r) => (
            <div
              key={r}
              className={`onboarding-chip ${
                selectedRadius === r ? "onboarding-chip-active" : ""
              }`}
              onClick={() => setSelectedRadius(r)}
            >
              {r} KM
            </div>
          ))}
        </div>
      </div>

      {/* ---------- BUFFER ---------- */}
      <div className="onboarding-form-group">
        <label>Extra Buffer (+km)</label>
        <div className="onboarding-chip-row">
          {[0, 1, 2, 3].map((b) => (
            <div
              key={b}
              className={`onboarding-chip ${
                bufferRadius === b ? "onboarding-chip-active" : ""
              }`}
              onClick={() => setBufferRadius(b)}
            >
              {b} KM
            </div>
          ))}
        </div>
      </div>

      {/* ---------- BUTTONS ---------- */}
      <div className="onboarding-button-group">
        <button
          type="button"
          className="onboarding-btn onboarding-btn-secondary"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="onboarding-btn onboarding-btn-success"
          disabled={!isFormComplete || saving}
          onClick={handleSubmit}
        >
          {saving ? "Saving..." : "Complete Onboarding"}
        </button>
      </div>
    </>
  );
};

export default R2rKitStep;
